import React, { useCallback, useMemo } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import {
  getTemplateHref,
  templateColumns,
} from "./tableParams/templateColumns";
import FastAPI from "../../../api/FastAPI";
import useFast from "../../../contexts/FASTContext";
import useFASTLayout from "../../../hooks/useFASTLayout";
import { WeekDays } from "../../../utils/fast";
import CreateButton from "../../common/CreateButton";
import DataTable from "../../dataTable/DataTable";
import { fastTemplatePath } from "../../routes/routes";
import useFastRowActionList from "../useFastRowActionList";

import type { JSX } from "react";

import type { GetTemplateResponse, Template } from "../../../api/FastAPI.types";
import type { FASTChannelRoutesParams } from "../../routes/FASTChannelRoutes";
import type { FastRowRights } from "../useFastRowActionList.types";

function selectTemplateRights(details: GetTemplateResponse): FastRowRights {
  return details;
}

function Templates(): JSX.Element {
  const { channelId } = useParams<FASTChannelRoutesParams>();
  const { t } = useTranslation("editor-fast-channel");
  const { state } = useFast();

  const queryClient = useQueryClient();

  useFASTLayout({
    title: t("templating"),
  });

  const getTemplatesListQuery = FastAPI.generateQuery({
    fn: FastAPI.getTemplatesList,
    params: [channelId],
  });

  const { isLoading, isError, refetch, data } = useQuery(getTemplatesListQuery);

  const { mutate: deleteTemplate } = useMutation({
    mutationFn: async (templateId: string) =>
      FastAPI.deleteTemplate(channelId, templateId),
    onSuccess: () => {
      toast.success(t("templateDeleteSuccess"));
      queryClient.invalidateQueries(getTemplatesListQuery.queryKey);
    },
    onError: (e) => {
      toast.error(t("templateDeleteError"));
      console.error(e);
    },
  });

  const { mutate: duplicateTemplate } = useMutation({
    mutationFn: async (templateId: string) =>
      FastAPI.duplicateTemplate(channelId, templateId),
    onSuccess: () => {
      toast.success(t("templateDuplicateSuccess"));
      queryClient.invalidateQueries(getTemplatesListQuery.queryKey);
    },
    onError: (e) => {
      toast.error(t("templateDuplicateError"));
      console.error(e);
    },
  });

  const { mutate: archiveTemplate } = useMutation({
    mutationFn: async (templateId: string) =>
      FastAPI.archiveTemplate(channelId, templateId),
    onSuccess: () => {
      toast.success(t("templateArchiveSuccess"));
      queryClient.invalidateQueries(getTemplatesListQuery.queryKey);
    },
    onError: (e) => {
      toast.error(t("templateArchiveError"));
      console.error(e);
    },
  });

  const getDetailsQuery = useCallback(
    (template: Template) =>
      FastAPI.generateQuery({
        fn: FastAPI.getTemplate,
        params: [channelId, template.uuid],
      }),
    [channelId],
  );

  const getEditPath = useCallback(
    (template: Template): string => getTemplateHref(channelId, template),
    [channelId],
  );

  const isDuplicateDisabled = useCallback(
    (template: Template): boolean => template.slots_errors_count !== 0,
    [],
  );

  const loadActions = useFastRowActionList({
    labelPrefix: "template",
    getDetailsQuery,
    selectRights: selectTemplateRights,
    getEditPath,
    isDuplicateDisabled,
    onDuplicate: duplicateTemplate,
    onArchive: archiveTemplate,
    onDelete: deleteTemplate,
  });

  const firstDayOfWeek =
    state.channelData?.first_day_of_week ?? WeekDays.MONDAY;

  const getRowHref = useCallback(
    (template: Template): string => getTemplateHref(channelId, template),
    [channelId],
  );

  const columnList = useMemo(
    () => templateColumns({ t, channelId, firstDayOfWeek, loadActions }),
    [t, channelId, firstDayOfWeek, loadActions],
  );

  const createTemplateButton = (
    <CreateButton
      href={fastTemplatePath(channelId, "new")}
      label={t("createTemplate")}
    />
  );

  return (
    <div className="w-full min-w-0">
      <DataTable
        columns={columnList}
        data={data?.data ?? []}
        defaultSorting={{ id: "updated_at", desc: true }}
        getRowHref={getRowHref}
        headerActions={createTemplateButton}
        isError={isError}
        isLoading={isLoading}
        onRetry={refetch}
        title={t("templatesListTitle")}
      />
    </div>
  );
}

export default Templates;
